import { type Dispatch, type SetStateAction } from "react";
import { Button } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import "@/entities/journal";
import { useAppRoutes } from "@shared/lib/react-router";
import "./section-third-actions.scss";

interface SectionThirdActionsProps {
  canSendToSign: boolean;
  canRejectSign: boolean;
  canRollback: boolean;
  canDelete: boolean;
  canEdit: boolean;
  onOpenVersionsList?: () => void;
  itemNoVersionId?: string | null;
  handleIsForm: (status: boolean) => void;
  setModalState?: Dispatch<SetStateAction<any>>;
  navigate?: (path: string) => void;
  projectId?: string;
  objectId?: string;
  mainJournalId?: string;
  titleId?: string;
  handleSendToSign?: (params: { itemNoVersionId: string }) => Promise<void>;
  handleStatusReturn?: (params: { itemNoVersionId: string }) => Promise<void>;
  handleReturn?: (params: { itemNoVersionId: string }) => Promise<void>;
  deleteSectionEntry?: (params: { params: { path: { itemNoVersionId: string } } }) => void;
  invalidateItem?: () => void;
}

export const SectionThirdActions = ({
  canSendToSign,
  canRejectSign,
  canRollback,
  canDelete,
  canEdit,
  onOpenVersionsList,
  itemNoVersionId,
  handleIsForm,
  setModalState,
  navigate: navigateProp,
  projectId,
  objectId,
  mainJournalId,
  titleId,
  handleSendToSign: handleSendToSignProp,
  handleStatusReturn: handleStatusReturnProp,
  handleReturn: handleReturnProp,
  deleteSectionEntry: deleteSectionEntryProp,
  invalidateItem,
}: SectionThirdActionsProps) => {
  const navigate1 = useNavigate();
  const navigate = navigateProp || navigate1;
  const [, setSearchParams] = useSearchParams();
  const appRoutes = useAppRoutes();

  const handleClose = () => {
    handleIsForm(false);
    if (setModalState) {
      setModalState((prev: any) => ({
        ...prev,
        itemNoVersionId: null,
        itemPartOne: null,
        itemPartTwo: null,
        signerEmployeeId: null,
        sectionTwoAction: null,
      }));
      setSearchParams({});
    }
  };

  const handleEdit = () => {
    if (navigateProp && projectId && objectId && mainJournalId && titleId) {
      navigate(
        appRoutes.journals.section3Form(projectId, objectId, mainJournalId, titleId, itemNoVersionId || ""),
      );
    } else {
      const params = new URLSearchParams();
      params.set("itemNoVersionId", itemNoVersionId || "");
      params.set("action", "EDIT");
      setSearchParams(params);
    }
  };

  return (
    <div className="journal-actions-bar">
      {canSendToSign && (
        <Button
          style={{ height: 44 }}
          onClick={async () => {
            if (handleSendToSignProp && itemNoVersionId) {
              await handleSendToSignProp({ itemNoVersionId });
              invalidateItem?.();
            } else if (setModalState) {
              setModalState((prev: any) => ({
                ...prev,
                visible: true,
                title: "Отправка записи на подписание",
                info: "Вы действительно хотите отправить запись на подписание?",
                confirmText: "Отправить",
                cancelText: "Отмена",
                isInputModal: false,
                action: "SEND",
                actionPayload: { itemNoVersionId },
              }));
            } else {
              const params = new URLSearchParams();
              params.set("itemNoVersionId", itemNoVersionId || "");
              params.set("action", "SEND");
              setSearchParams(params);
            }
          }}
        >
          Отправить на подписание
        </Button>
      )}
      {itemNoVersionId && onOpenVersionsList ? (
        <Button style={{ height: 44 }} onClick={onOpenVersionsList}>
          Просмотреть версии
        </Button>
      ) : null}
      {canRejectSign && (
        <Button
          style={{ height: 44 }}
          onClick={async () => {
            if (handleStatusReturnProp && itemNoVersionId) {
              await handleStatusReturnProp({ itemNoVersionId });
              invalidateItem?.();
            } else if (setModalState) {
              setModalState((prev: any) => ({
                ...prev,
                visible: true,
                title: "Отозвать с подписания",
                info: "Вы действительно хотите отменить подписание?",
                confirmText: "Отозвать",
                cancelText: "Отмена",
                isInputModal: false,
                action: "RECALL",
                actionPayload: { itemNoVersionId },
              }));
            } else {
              const params = new URLSearchParams();
              params.set("itemNoVersionId", itemNoVersionId || "");
              params.set("action", "RECALL");
              setSearchParams(params);
            }
          }}
        >
          Отозвать с подписания
        </Button>
      )}
      {canRollback && (
        <Button
          style={{ height: 44 }}
          onClick={async () => {
            if (handleReturnProp && itemNoVersionId) {
              await handleReturnProp({ itemNoVersionId });
              invalidateItem?.();
            } else if (setModalState) {
              setModalState((prev: any) => ({
                ...prev,
                visible: true,
                title: "Вернуть запись в работу",
                info: "Вы действительно хотите вернуть запись в статус Новый?",
                confirmText: "Вернуть",
                cancelText: "Отмена",
                isInputModal: false,
                action: "RETURN",
                actionPayload: { itemNoVersionId },
              }));
            } else {
              const params = new URLSearchParams();
              params.set("itemNoVersionId", itemNoVersionId || "");
              params.set("action", "RETURN");
              setSearchParams(params);
            }
          }}
        >
          Вернуть в работу
        </Button>
      )}
      {canDelete && (
        <Button
          style={{ height: 44 }}
          onClick={() => {
            if (deleteSectionEntryProp && itemNoVersionId) {
              deleteSectionEntryProp({
                params: {
                  path: { itemNoVersionId },
                },
              });
              invalidateItem?.();
              if (navigateProp && projectId && objectId && mainJournalId && titleId) {
                navigate(
                  appRoutes.journals.section3(projectId, objectId, mainJournalId, titleId),
                );
              }
            } else if (setModalState) {
              setModalState((prev: any) => ({
                ...prev,
                visible: true,
                title: "Удаление записи",
                info: "Вы действительно хотите удалить запись?",
                confirmText: "Удалить",
                cancelText: "Отмена",
                isInputModal: false,
                action: "DELETE",
                actionPayload: { itemNoVersionId },
              }));
            } else {
              const params = new URLSearchParams();
              params.set("itemNoVersionId", itemNoVersionId || "");
              params.set("action", "DELETE");
              setSearchParams(params);
            }
          }}
        >
          Удалить
        </Button>
      )}
      {canEdit && (
        <Button style={{ height: 44 }} onClick={handleEdit}>
          Редактировать
        </Button>
      )}
      <Button type="primary" onClick={handleClose} style={{ height: 44 }}>
        Закрыть
      </Button>
    </div>
  );
};

